import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import ReactMarkdown from "react-markdown";
import {
  LinkedinLogo, Sparkle, Robot, ArrowsClockwise, WarningCircle, CheckCircle, Copy,
} from "@phosphor-icons/react";

/**
 * LinkedIn-profiel stappenplan — AI-gegenereerd, gepersonaliseerd plan
 * dat de coachee stap-voor-stap door alle LinkedIn-onderdelen leidt
 * (foto, headline, about, ervaring, skills, open-to-work, etc.).
 * Max 3 AI-generaties in totaal.
 */
export default function CoacheeLinkedinPlanPage() {
  const [coachee, setCoachee] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const pollRef = React.useRef(null);

  // Register with global AI-task registry so the floating pill stays
  // visible if the user navigates away while generation is running.
  useAITaskRegistration({
    active: generating && !!coachee,
    id: coachee ? `linkedin-${coachee.id}` : null,
    label: "LinkedIn-plan schrijven",
    returnTo: "/profielen/linkedin",
  });

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const startPolling = (coacheeId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: fresh } = await api.get(`/coachees/${coacheeId}/linkedin-plan`);
        setPlan(fresh);
        if (fresh.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          if (fresh.status === "failed") {
            setError(fresh.error || "De AI kon je stappenplan niet genereren. Probeer het opnieuw.");
          }
        }
      } catch (err) {
        // Network blips zijn ok — polling gaat door
        console.warn("LinkedIn poll error:", err?.message || err);
      }
    }, 3000);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (!own) { setLoading(false); return; }
        const { data } = await api.get(`/coachees/${own.id}/linkedin-plan`);
        setPlan(data);
        // Resume polling als er een running job is bij page-load
        if (data.status === "running") {
          setGenerating(true);
          startPolling(own.id);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    if (!coachee || generating) return;
    setError("");
    setGenerating(true);
    setConfirmRegen(false);
    try {
      // Background-task pattern: POST keert direct terug met status='running'.
      // Verbinding via Cloudflare/K8s blijft dus < 1s open en kan niet droppen.
      const { data } = await api.post(
        `/coachees/${coachee.id}/linkedin-plan/generate`,
        {},
        { timeout: 30000 },
      );
      setPlan(data);
      if (data.status === "running") {
        startPolling(coachee.id);
      } else {
        setGenerating(false);
        if (data.status === "failed") {
          setError(data.error || "De AI kon je stappenplan niet genereren.");
        }
      }
    } catch (err) {
      setGenerating(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleCopy = async () => {
    if (!plan?.plan) return;
    try {
      await navigator.clipboard.writeText(plan.plan);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("Copy failed:", err?.message || err);
    }
  };

  if (loading) {
    return <AppLayout title="LinkedIn-stappenplan"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }
  if (generating) {
    return (
      <AppLayout title="LinkedIn-stappenplan" breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "LinkedIn-stappenplan" }]}>
        <div className="max-w-2xl" data-testid="li-ai-running">
          <div className="neuro-card-bold p-8 bg-white">
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex h-14 w-14 items-center justify-center bg-[#0A66C2]/10 text-[#0A66C2] rounded-sm animate-pulse">
                <Robot size={30} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#0A66C2] mb-1">Even geduld</div>
                <h2 className="font-display text-xl font-bold tracking-tight">
                  De AI schrijft je LinkedIn-stappenplan...
                </h2>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  We combineren je CV, breintype, tests en profielen tot een persoonlijk
                  stappenplan: foto, headline, about-tekst, ervaring, skills, open-to-work
                  en netwerk-strategie. Dit duurt ongeveer 30-60 seconden.
                </p>
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                  💡 <strong>Je kunt gerust doorwerken.</strong> Klik in de zijbalk naar een
                  andere pagina — de AI werkt op de achtergrond door en het plan staat hier
                  klaar zodra je terugkomt.
                </p>
                <div className="mt-4 flex gap-1">
                  <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const used = plan?.ai_count ?? 0;
  const max = plan?.ai_max ?? 3;
  const remaining = plan?.ai_remaining ?? Math.max(0, max - used);
  const hasPlan = !!(plan?.plan || "").trim();

  return (
    <AppLayout
      title="LinkedIn-stappenplan"
      breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "LinkedIn-stappenplan" }]}
    >
      <div className="max-w-3xl space-y-6 pb-12" data-testid="linkedin-plan-page">
        {/* Intro */}
        <div className="neuro-card-bold p-5 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#0A66C2]/10 text-[#0A66C2] rounded-sm">
              <LinkedinLogo size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight">Jouw LinkedIn-stappenplan</h2>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Een persoonlijk, stap-voor-stap actieplan om je LinkedIn-profiel te
                optimaliseren. Gebaseerd op je CV, breintype, tests en de drie profielen
                die je al hebt ingevuld. Inclusief kant-en-klare voorbeeldteksten.
              </p>
              <div className="mt-3 text-xs text-zinc-600">
                <strong>AI-gebruik: {used} van {max}</strong>
                {remaining > 0
                  ? <> — nog <strong>{remaining}</strong> {remaining === 1 ? "keer" : "keer"} te genereren.</>
                  : <> — limiet bereikt.</>}
                {plan?.generated_at && (
                  <span className="ml-2 text-zinc-400">
                    · laatst gegenereerd: {new Date(plan.generated_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="li-error">
            <WarningCircle size={18} className="shrink-0 mt-0.5" /> <div>{error}</div>
          </div>
        )}

        {plan?.status === "failed" && (
          <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="li-ai-failed">
            <Sparkle size={18} weight="duotone" className="shrink-0 mt-0.5 text-amber-600" />
            <div>
              <div className="font-medium">De AI kon je LinkedIn-stappenplan deze keer niet genereren.</div>
              {plan.error && (
                <div className="mt-1 text-xs text-amber-900/80" data-testid="li-ai-failed-detail">
                  {plan.error}
                </div>
              )}
              <div className="mt-1 text-xs">Probeer het zo opnieuw.</div>
            </div>
          </div>
        )}

        {/* Action bar */}
        {!hasPlan ? (
          <div className="neuro-card p-6 bg-white text-center" data-testid="li-empty">
            <Sparkle size={32} weight="duotone" className="text-[#0A66C2] mx-auto mb-3" />
            <p className="text-sm text-zinc-600 max-w-md mx-auto mb-4 leading-relaxed">
              Klik op de knop hieronder om je persoonlijke LinkedIn-stappenplan te
              laten genereren. De AI gebruikt al je tests en profielen om er iets
              passends van te maken.
            </p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={remaining <= 0}
              data-testid="li-generate-btn"
              className="inline-flex items-center gap-2 bg-[#0A66C2] text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#084d92] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <Sparkle size={16} weight="duotone" /> Genereer mijn stappenplan
            </button>
          </div>
        ) : (
          <>
            {/* Plan rendering */}
            <div className="neuro-card-bold p-6 bg-white" data-testid="li-plan-content">
              <div className="prose prose-sm max-w-none prose-zinc
                prose-headings:font-display prose-headings:tracking-tight
                prose-h2:text-xl prose-h2:font-bold
                prose-h2:mt-8 prose-h2:mb-4 prose-h2:pb-2
                prose-h2:border-b-2 prose-h2:border-[#0A66C2]
                prose-h2:first:mt-0
                prose-h3:text-base prose-h3:font-semibold
                prose-h3:mt-5 prose-h3:mb-2
                prose-p:my-3 prose-p:leading-relaxed
                prose-ul:my-3 prose-ol:my-3
                prose-li:my-1 prose-li:leading-relaxed
                prose-strong:text-zinc-900 prose-strong:font-semibold
                prose-em:text-zinc-700
                prose-blockquote:not-italic prose-blockquote:my-4
                prose-blockquote:bg-[#0A66C2]/5
                prose-blockquote:border-l-4 prose-blockquote:border-[#0A66C2]
                prose-blockquote:py-2 prose-blockquote:px-4
                prose-blockquote:rounded-r-sm
                prose-blockquote:font-normal
                prose-hr:my-6">
                <ReactMarkdown>{plan.plan}</ReactMarkdown>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-between">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="li-copy-btn"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-300 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:border-zinc-900 transition-colors"
              >
                <Copy size={14} weight={copied ? "fill" : "regular"} /> {copied ? "Gekopieerd!" : "Kopieer plan"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmRegen(true)}
                disabled={remaining <= 0}
                data-testid="li-regenerate-btn"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors disabled:border-zinc-300 disabled:text-zinc-400 disabled:hover:bg-white disabled:cursor-not-allowed"
              >
                <ArrowsClockwise size={14} weight="bold" /> Opnieuw genereren ({remaining} over)
              </button>
            </div>
          </>
        )}

        {confirmRegen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="li-regen-modal" role="dialog" aria-modal="true">
            <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
              <div className="flex items-start gap-3 mb-3">
                <WarningCircle size={24} weight="duotone" className="text-[#0A66C2] shrink-0 mt-0.5" />
                <h3 className="font-display text-lg font-bold tracking-tight">Stappenplan opnieuw genereren?</h3>
              </div>
              <ul className="text-sm text-zinc-700 leading-relaxed space-y-2 mb-3 list-disc pl-5">
                <li>Je huidige stappenplan wordt <strong>overschreven</strong>.</li>
                <li>De AI gebruikt je nieuwste gegevens en profielen.</li>
              </ul>
              <div className="text-xs text-zinc-500 mb-5">
                Na deze run heb je nog <strong>{Math.max(0, remaining - 1)}</strong> AI-pogingen over.
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setConfirmRegen(false)} data-testid="li-regen-cancel"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors">
                  Annuleer
                </button>
                <button type="button" onClick={handleGenerate} data-testid="li-regen-confirm"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#0A66C2] transition-colors inline-flex items-center gap-2">
                  <ArrowsClockwise size={14} weight="bold" /> Ja, opnieuw genereren
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => navigate("/profielen")}
            data-testid="li-back"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#0A66C2] transition-colors"
          >
            <CheckCircle size={16} /> Terug naar profielen
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

function Dot({ delay }) {
  return <span className="inline-block w-2 h-2 rounded-full bg-[#0A66C2] animate-pulse" style={{ animationDelay: `${delay}ms` }} />;
}
